import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { CheckCircle, Desktop, DeviceMobile, PencilSimple } from "@phosphor-icons/react";
import { previewUrl } from "@/components/thankyou/ThankYouPreviewDialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PILL = "border-2 border-black px-2 py-1 text-[10px] uppercase tracking-widest inline-flex items-center gap-1 transition-colors duration-100";
const pill = (on) => `${PILL} ${on ? "bg-black text-white" : "bg-white hover:bg-zinc-100"}`;
const BTN = "border-2 border-black px-4 py-2 text-xs uppercase tracking-widest inline-flex items-center gap-1.5 disabled:opacity-40 transition-colors duration-100";

function ReplyForm({ token, onDone }) {
  const [mode, setMode] = useState(null);
  const [name, setName] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const send = async (decision) => {
    setBusy(true);
    setError("");
    try {
      const { data } = await axios.post(`${API}/public/ty-review/${encodeURIComponent(token)}/respond`, { decision, name: name || null, note: note || null });
      onDone(data.response);
    } catch (e) {
      setError(e?.response?.data?.detail || "That didn't go through. Please try again.");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="border-2 border-black bg-white p-4" data-testid="ty-review-reply">
      <label className="block text-xs">Your name (optional)
        <input value={name} onChange={(e) => setName(e.target.value)} maxLength={80} className="mt-1 w-full border-2 border-black px-2 py-1.5 text-sm" data-testid="ty-review-name" />
      </label>
      {mode === "changes" && (
        <label className="mt-3 block text-xs">What should we change?
          <textarea value={note} onChange={(e) => setNote(e.target.value)} maxLength={2000} rows={4} className="mt-1 w-full border-2 border-black px-2 py-1.5 text-sm" data-testid="ty-review-note" />
        </label>
      )}
      {error && <div className="mt-2 text-xs text-red-700" data-testid="ty-review-error">{error}</div>}
      <div className="mt-3 flex flex-wrap gap-2">
        <button type="button" disabled={busy} onClick={() => send("approved")} className={`${BTN} bg-black text-white hover:bg-emerald-700`} data-testid="ty-review-approve">
          <CheckCircle size={14} weight="bold" /> Looks good, approve
        </button>
        {mode === "changes" ? (
          <button type="button" disabled={busy || !note.trim()} onClick={() => send("changes")} className={`${BTN} bg-white hover:bg-amber-100`} data-testid="ty-review-send-changes">
            Send my changes
          </button>
        ) : (
          <button type="button" onClick={() => setMode("changes")} className={`${BTN} bg-white hover:bg-amber-100`} data-testid="ty-review-request-changes">
            <PencilSimple size={14} weight="bold" /> Ask for changes
          </button>
        )}
      </div>
    </div>
  );
}

function Done({ r }) {
  return (
    <div className="border-2 border-black bg-emerald-50 p-4 text-sm" data-testid="ty-review-done">
      <strong>{r.decision === "approved" ? "Thanks, you approved this page." : "Thanks, we got your changes."}</strong>
      <div className="mt-1 text-xs text-zinc-700">{r.decision === "approved" ? "We'll keep it as is." : "We'll update the page and may send you a new link."}</div>
    </div>
  );
}

/** Public, view-only: a firm looks at its own thank-you page, then approves or asks for changes. */
export default function TyReviewPage() {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [error, setError] = useState("");
  const [funnel, setFunnel] = useState("");
  const [phone, setPhone] = useState(false);
  const [reply, setReply] = useState(null);
  useEffect(() => {
    axios.get(`${API}/public/ty-review/${encodeURIComponent(token)}`)
      .then((r) => { setInfo(r.data); setFunnel(r.data.funnels[0]?.slug || "standard"); })
      .catch((e) => setError(e?.response?.data?.detail || "This preview link isn't available."));
  }, [token]);
  if (error) return <div className="mx-auto max-w-xl p-8 text-center" data-testid="ty-review-error-page"><h1 className="font-chivo text-2xl font-black">Link not available</h1><p className="mt-2 text-sm">{error}</p></div>;
  if (!info) return <div className="label-mono p-8" data-testid="ty-review-loading">Loading…</div>;
  const url = previewUrl(info.code, funnel, token);
  return (
    <div className="min-h-screen bg-zinc-100" data-testid="ty-review-page">
      <div className="mx-auto max-w-6xl p-4 sm:p-6">
        <div className="label-mono text-zinc-500">Private preview · view only</div>
        <h1 className="font-chivo text-3xl font-black tracking-tight sm:text-4xl">{info.firm_name}: your thank-you page</h1>
        <p className="mt-2 max-w-3xl text-sm text-zinc-700">
          This is what someone sees right after their case is sent to you. It changes a little per page, so you can switch below.
          Nothing here is tracked or counted.
        </p>
        <div className="mt-4 flex flex-wrap items-center gap-1.5">
          {info.funnels.map((f) => (
            <button key={f.slug} type="button" onClick={() => setFunnel(f.slug)} className={pill(f.slug === funnel)} data-testid={`ty-review-funnel-${f.slug}`}>{f.title}</button>
          ))}
          <span className="ml-auto inline-flex gap-1.5">
            <button type="button" onClick={() => setPhone(false)} className={pill(!phone)} data-testid="ty-review-desktop"><Desktop size={11} weight="bold" /> Desktop</button>
            <button type="button" onClick={() => setPhone(true)} className={pill(phone)} data-testid="ty-review-phone"><DeviceMobile size={11} weight="bold" /> Phone</button>
          </span>
        </div>
        <div className="mt-2 flex justify-center bg-zinc-200 p-2">
          <iframe key={url} title="Thank-you page preview" src={url} data-testid="ty-review-frame"
            className={`h-[65vh] border-2 border-black bg-white ${phone ? "w-[390px]" : "w-full"}`} />
        </div>
        <div className="mt-4">{reply ? <Done r={reply} /> : <ReplyForm token={token} onDone={setReply} />}</div>
      </div>
    </div>
  );
}
